import React, { useMemo, useState } from "react";
import {
  ArcadeActionButton,
  ArcadeArenaCard,
  ArcadeArenaStage,
  ArcadeBlockedSlot,
  ArcadeKeyframes,
  ArcadeLogPanel,
  ArcadeMissionPrompt,
  ArcadePlayfieldFrame,
  ArcadeStatusDots,
  FlashOverlay,
  createArcadeHudStripProps,
  pointsForHit,
  rankForScore,
  timeBonus,
  useArcadeActionHotkeys,
  useArcadeAutoClock,
  useArcadeClock,
  useReducedMotion,
} from "../../../games/index.js";
import {
  SQUARE_STITCHER_LEVELS,
  createSquareStitcherState,
  freeDiagonalWeldGenerated,
  getSquareStitcherLevel,
  legalSquareStitcherMoves,
  squareStitcherProgress,
  stepSquareStitcher,
  type SquareStitcherMove,
  type SquareStitcherState,
} from "../algorithms/squareStitcher.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  SquareStitcherGrid,
  verbForSquareStitcherMove,
} from "./square-stitcher-panel/SquareStitcherGrid.js";

const ROUND_SECONDS = 52;
const STITCH_POINTS = 26;
const ROTATE_POINTS = 5;

type FlashTone = "rotate" | "stitch" | "clear" | null;

export function SquareStitcherPanel(): React.JSX.Element {
  const [state, setState] = useState<SquareStitcherState>(() =>
    createSquareStitcherState(),
  );
  const [combo, setCombo] = useState(0);
  const [score, setScore] = useState(0);
  const [flashTone, setFlashTone] = useState<FlashTone>(null);
  const [flashToken, setFlashToken] = useState(0);
  const clock = useArcadeClock(ROUND_SECONDS);
  const { pause: pauseClock, reset: resetClock, start: startClock } = clock;
  const reducedMotion = useReducedMotion();
  const level = getSquareStitcherLevel(state.levelId);
  const moves = useMemo(() => legalSquareStitcherMoves(state), [state]);
  const currentMove = moves.at(0);
  const stitchBySquareId = useMemo(
    () =>
      new Map(
        moves
          .filter((move) => move.type === "stitch")
          .map((move) => [move.squareId, move]),
      ),
    [moves],
  );
  const rotateBySquareId = useMemo(
    () =>
      new Map(
        moves
          .filter((move) => move.type === "rotate")
          .map((move) => [move.squareId, move]),
      ),
    [moves],
  );
  const progress = squareStitcherProgress(state);

  const showFlash = (tone: Exclude<FlashTone, null>): void => {
    setFlashTone(tone);
    setFlashToken((token) => token + 1);
  };

  const reset = (levelId = state.levelId): void => {
    setState(createSquareStitcherState(levelId));
    setCombo(0);
    setScore(0);
    setFlashTone(null);
    setFlashToken(0);
    resetClock(ROUND_SECONDS);
    startClock();
  };

  const play = (move: SquareStitcherMove): void => {
    const legal = moves.some(
      (candidate) =>
        candidate.type === move.type && candidate.squareId === move.squareId,
    );
    if (!legal) return;
    const next = stepSquareStitcher(state, move);
    const stitched = move.type === "stitch";
    const nextCombo = stitched ? combo + 1 : Math.max(1, combo);
    const completed = next.status === "cleared";
    setState(next);
    setCombo(nextCombo);
    setScore(
      (value) =>
        value +
        (stitched ? pointsForHit(nextCombo, STITCH_POINTS) : ROTATE_POINTS) +
        (completed ? timeBonus(clock.remaining) : 0),
    );
    showFlash(completed ? "clear" : move.type);
  };

  const playCurrentMove = (): void => {
    if (currentMove) play(currentMove);
  };

  useArcadeActionHotkeys({
    primaryEnabled: state.status === "playing" && currentMove !== undefined,
    onPrimary: playCurrentMove,
    onReset: reset,
  });

  useArcadeAutoClock({
    active: state.status === "playing",
    onPause: pauseClock,
    onStart: startClock,
  });

  return (
    <PanelSurface.PanelShell
      title="Square Stitcher"
      subtitle="Rotate 2-cell squares until their unit or counit faces the target seam, then stitch the grid."
      accentColor={panelTokens.accent}
    >
      <ArcadeKeyframes />
      <ArcadeArenaCard glowColor={`${panelTokens.accent}30`}>
        <div className="relative overflow-hidden rounded-2xl">
          <ArcadeArenaStage
            hudOverlay={
              <FlashOverlay
                token={flashToken}
                color={
                  flashTone === "clear"
                    ? panelTokens.accentGreen
                    : flashTone === "rotate"
                      ? panelTokens.accentAmber
                      : panelTokens.accent
                }
                reducedMotion={reducedMotion}
              />
            }
            hudProps={createArcadeHudStripProps({
              stats: [
                {
                  label: "stitched",
                  value: `${state.stitchedSquareIds.length}/${level.targetStitches}`,
                  color: panelTokens.accent,
                },
                {
                  label: "grid",
                  value: `${progress}%`,
                  color: panelTokens.accentAmber,
                },
                {
                  label: "weld",
                  value: freeDiagonalWeldGenerated() ? "on" : "off",
                  color: panelTokens.accentRed,
                },
              ],
              score,
              rank: rankForScore(score),
              rankColor: panelTokens.accentGreen,
              scoreColor: panelTokens.bright,
              borderColor: panelTokens.border,
              clock,
              timerAccent: panelTokens.accent,
              timerDanger: panelTokens.accentRed,
              timerTrack: "rgba(148,163,184,0.18)",
              combo,
              comboColor: panelTokens.accent,
              comboMuted: panelTokens.muted,
              reducedMotion,
            })}
            levelSelectProps={{
              levels: SQUARE_STITCHER_LEVELS,
              activeLevelId: state.levelId,
              onSelect: reset,
              onReset: () => {
                reset();
              },
              accentColor: panelTokens.accent,
              borderColor: panelTokens.border,
              mutedColor: panelTokens.muted,
              brightColor: panelTokens.bright,
              activeBackground: `${panelTokens.accent}22`,
              background: "rgba(5,8,16,0.44)",
            }}
          >
            <ArcadePlayfieldFrame
              borderColor={panelTokens.border}
              background="rgba(5,8,16,0.9)"
              accentColor={panelTokens.accent}
              secondaryColor={panelTokens.accentAmber}
              gridSize={46}
              minHeight={560}
              header={
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <PanelSurface.SectionHeading>
                    2-cell stitch grid
                  </PanelSurface.SectionHeading>
                  <PanelSurface.Badge color={panelTokens.muted}>
                    align before stitch
                  </PanelSurface.Badge>
                </div>
              }
            >
              <div className="mb-4">
                <ArcadeMissionPrompt
                  label="next move"
                  title={
                    currentMove
                      ? `${verbForSquareStitcherMove(currentMove)} ${
                          currentMove.label
                        }`
                      : "Grid stitched"
                  }
                  detail={
                    currentMove?.type === "stitch"
                      ? "This square faces its target seam, so the stitch move exists."
                      : currentMove
                        ? "Rotate the square until its arrow faces the target seam."
                        : "Every square has been stitched with matching orientation."
                  }
                  color={
                    currentMove?.type === "rotate"
                      ? panelTokens.accentAmber
                      : panelTokens.accent
                  }
                  hotkey={currentMove ? "Space" : undefined}
                  status={currentMove?.type ?? state.status}
                />
              </div>
              <SquareStitcherGrid
                state={state}
                squares={level.squares}
                stitchBySquareId={stitchBySquareId}
                rotateBySquareId={rotateBySquareId}
                activeSquareId={currentMove?.squareId}
                onMove={play}
              />
              <ArcadeBlockedSlot
                label="Absent weld"
                title="Free diagonal stitch"
                detail="An unaligned square cannot be sealed by a shortcut. The stitch command exists only after the square's orientation matches the seam."
                color={panelTokens.accentRed}
                className="mt-4"
              />
            </ArcadePlayfieldFrame>

            <ArcadeLogPanel
              title="Stitch log"
              event={state.lastEvent}
              borderColor={panelTokens.border}
              background="rgba(5,8,16,0.68)"
              textColor={panelTokens.text}
              badges={[
                PanelSurface.badge("SDP grid", panelTokens.accent),
                PanelSurface.badge("2-cell", panelTokens.accentAmber),
              ]}
            >
              <PanelSurface.Stack>
                <ArcadeStatusDots
                  items={[
                    {
                      label: "aligned square",
                      active: moves.some((move) => move.type === "stitch"),
                    },
                    {
                      label: "grid complete",
                      active: state.status === "cleared",
                    },
                    {
                      label: "weld absent",
                      active: !freeDiagonalWeldGenerated(),
                    },
                  ]}
                  activeColor={panelTokens.accentGreen}
                  inactiveColor={panelTokens.muted}
                />
                <div className="grid gap-2">
                  <ArcadeActionButton
                    color={currentMove ? panelTokens.accent : panelTokens.muted}
                    disabled={!currentMove}
                    onClick={playCurrentMove}
                    ariaLabel="Perform next square stitcher move"
                    icon=">"
                  >
                    {currentMove
                      ? verbForSquareStitcherMove(currentMove)
                      : "No move"}
                  </ArcadeActionButton>
                  <div className="grid grid-cols-2 gap-2">
                    <ArcadeActionButton
                      color={panelTokens.accentAmber}
                      onClick={() => {
                        if (clock.running) {
                          pauseClock();
                        } else {
                          startClock();
                        }
                      }}
                      ariaLabel={clock.running ? "Pause timer" : "Run timer"}
                      icon={clock.running ? "||" : ">"}
                    >
                      {clock.running ? "Pause" : "Run"}
                    </ArcadeActionButton>
                    <ArcadeActionButton
                      color={panelTokens.accentRed}
                      onClick={() => {
                        reset();
                      }}
                      ariaLabel="Reset Square Stitcher"
                      icon="R"
                    >
                      Reset
                    </ArcadeActionButton>
                  </div>
                </div>
              </PanelSurface.Stack>
            </ArcadeLogPanel>
          </ArcadeArenaStage>
        </div>
      </ArcadeArenaCard>
    </PanelSurface.PanelShell>
  );
}
